/**
 * PaymentSignUp Component
 * 
 * Modal component for handling BridgePal Kids subscription sign-up using PayPal.
 * Displays two subscription options (Monthly and Annual) and integrates with
 * PayPal's JavaScript SDK for secure subscription creation.
 * 
 * Props:
 * - user: Current authenticated user object
 * - theme: Theme object containing primaryColor and other styling
 * - open: Boolean controlling modal visibility
 * - handleClose: Callback function to close the modal
 * 
 * Environment Variables Required:
 * - REACT_APP_PAYPAL_CLIENT_ID: PayPal client ID
 * - REACT_APP_PAYPAL_PLAN_MONTHLY: PayPal monthly plan ID
 * - REACT_APP_PAYPAL_PLAN_ANNUAL: PayPal annual plan ID
 * - REACT_APP_API_URL: Backend API base URL
 */

import { useCallback, useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Modal from "@material-ui/core/Modal";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Button from "../Input/Button";
import "./PaymentSignup.css";

// Environment configuration
const { 
  REACT_APP_API_URL, 
  REACT_APP_PAYPAL_CLIENT_ID,
  REACT_APP_PAYPAL_PLAN_MONTHLY,
  REACT_APP_PAYPAL_PLAN_ANNUAL,
} = process.env;

// PayPal SDK initialization options
const PAYPAL_OPTIONS = {
  "client-id": REACT_APP_PAYPAL_CLIENT_ID,
  vault: true,
  intent: "subscription",
};

// Subscription plan configuration
const SUBSCRIPTION_PLANS = {
  monthly: {
    id: REACT_APP_PAYPAL_PLAN_MONTHLY,
    name: "Monthly",
    price: "$11.99",
    priceValue: 11.99,
    description: "per month",
    type: "monthly",
  },
  annual: {
    id: REACT_APP_PAYPAL_PLAN_ANNUAL,
    name: "Annual",
    price: "$99.99",
    priceValue: 99.99,
    description: "per year",
    savingsText: "Save 30%!",
    type: "annual",
  },
};

/**
 * PaymentSignUp Modal Component
 */
export default function PaymentSignUp(props) {
  const { user, theme, open, handleClose } = props;

  // Component state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stage, setStage] = useState(1); // 1: Select plan, 2: Payment, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dynamic styles based on theme
  const useStyles = makeStyles((theme2) => ({
    modal: {
      backgroundColor: theme2.palette.background.paper,
      border: "40px solid transparent",
      borderTop: "none",
      borderBottom: "none",
      boxShadow: theme2.shadows[5],
      padding: theme2.spacing(3),
      position: "absolute",
      width: 500,
      minHeight: 400,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      borderRadius: "12px",
      outline: "none",
    },
    title: {
      fontSize: 28,
      fontFamily: "GoodUnicorn",
      color: theme?.primaryColor || "#4a90d9",
      textAlign: "center",
      marginBottom: theme2.spacing(2),
    },
    subtitle: {
      fontSize: 14,
      color: "#666",
      textAlign: "center",
      marginBottom: theme2.spacing(3),
    },
    planCard: {
      margin: "10px",
      padding: "20px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderRadius: "12px",
      flex: 1,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: theme2.shadows[4],
      },
    },
    planCardSelected: {
      margin: "10px",
      padding: "20px",
      cursor: "pointer",
      borderRadius: "12px",
      border: `3px solid ${theme?.primaryColor || "#4a90d9"}`,
      backgroundColor: `${theme?.primaryColor || "#4a90d9"}10`,
      flex: 1,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    },
    planName: {
      fontSize: "20px",
      textAlign: "center",
      fontWeight: "bold",
      marginBottom: "8px",
    },
    planPrice: {
      fontSize: "24px",
      textAlign: "center",
      color: theme?.primaryColor || "#4a90d9",
      fontWeight: "bold",
    },
    planDescription: {
      fontSize: "12px",
      textAlign: "center",
      color: "#888",
    },
    savingsBadge: {
      backgroundColor: "#4CAF50",
      color: "white",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: "bold",
      textAlign: "center",
      marginTop: "8px",
    },
    buttonContainer: {
      width: "100%",
      textAlign: "center",
      marginTop: theme2.spacing(3),
    },
    paypalContainer: {
      marginTop: theme2.spacing(3),
      padding: theme2.spacing(2),
    },
    errorText: {
      color: "#f44336",
      textAlign: "center",
      marginTop: theme2.spacing(2),
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: theme2.spacing(4),
    },
    successContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: theme2.spacing(4),
    },
    successIcon: {
      fontSize: 64,
      color: "#4CAF50",
      marginBottom: theme2.spacing(2),
    },
    backButton: {
      marginTop: theme2.spacing(2),
      color: "#666",
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  }));

  const classes = useStyles();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStage(1);
      setSelectedPlan(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  /**
   * Get authorization token from local storage
   * Uses JWT_TOKEN key which is set by the axiosConfig TokenManager
   * @returns {string|null} JWT token or null
   */
  const getAuthToken = useCallback(() => {
    return localStorage.getItem("JWT_TOKEN");
  }, []);

  /**
   * Handle subscription plan selection
   * @param {string} planKey - 'monthly' or 'annual'
   */
  const handlePlanSelect = useCallback((planKey) => {
    setSelectedPlan(planKey);
    setError(null);
  }, []);

  /**
   * Proceed to payment stage
   */
  const handleProceedToPayment = useCallback(() => {
    if (!selectedPlan) {
      setError("Please select a subscription plan");
      return;
    }
    setStage(2);
    setError(null);
  }, [selectedPlan]);

  /**
   * Go back to plan selection
   */
  const handleBackToSelection = useCallback(() => {
    setStage(1);
    setError(null);
  }, []);

  /**
   * Create PayPal subscription
   * Called by PayPal SDK when user clicks the PayPal button
   * @param {object} data - PayPal data object
   * @param {object} actions - PayPal actions object
   * @returns {Promise<string>} Subscription ID
   */
  const createSubscription = useCallback((data, actions) => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];
    
    if (!plan || !plan.id) {
      console.error("PayPal: Invalid plan configuration", { selectedPlan, plan });
      throw new Error("Invalid subscription plan");
    }

    console.log("PayPal: Creating subscription", {
      planId: plan.id,
      planType: plan.type,
    });

    return actions.subscription.create({
      plan_id: plan.id,
    });
  }, [selectedPlan]);

  /**
   * Handle successful PayPal subscription approval
   * Sends subscription details to backend for activation
   * @param {object} data - PayPal approval data containing subscriptionID
   * @param {object} actions - PayPal actions object
   */
  const onSubscriptionApprove = useCallback(async (data, actions) => {
    console.log("PayPal: Subscription approved", {
      subscriptionId: data.subscriptionID,
      planType: selectedPlan,
    });

    setLoading(true);
    setError(null);

    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error("Not authenticated. Please log in and try again.");
      }

      // Send subscription details to backend for activation
      const response = await fetch(`${REACT_APP_API_URL}/paypal/subscription/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          subscriptionId: data.subscriptionID,
          planType: SUBSCRIPTION_PLANS[selectedPlan].type,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to activate subscription");
      }

      console.log("PayPal: Subscription activated successfully", result);
      
      // Show success stage
      setStage(3);
      setLoading(false);

    } catch (err) {
      console.error("PayPal: Subscription activation failed", err);
      setError(err.message || "Failed to activate subscription. Please contact support.");
      setLoading(false);
    }
  }, [selectedPlan, getAuthToken]);

  /**
   * Handle PayPal errors
   * @param {Error} err - PayPal error object
   */
  const onSubscriptionError = useCallback((err) => {
    console.error("PayPal: Subscription error", err);
    setError("PayPal encountered an error. Please try again.");
    setLoading(false);
  }, []);

  /**
   * Handle PayPal cancellation
   * @param {object} data - PayPal cancellation data
   */
  const onSubscriptionCancel = useCallback((data) => {
    console.log("PayPal: Subscription cancelled by user", data);
    setError("Payment was cancelled. Select a plan and try again.");
  }, []);

  /**
   * Render Plan Selection Stage (Stage 1)
   */
  const renderPlanSelection = () => (
    <div className={classes.modal}>
      <Typography className={classes.title}>
        Choose Your Subscription
      </Typography>
      <Typography className={classes.subtitle}>
        Select a plan to access BridgePal Kids
      </Typography>

      <Grid container justifyContent="center" alignItems="stretch" spacing={0}>
        {/* Monthly Plan */}
        <Grid item xs={6} style={{ display: "flex", justifyContent: "center" }}>
          <Card
            onClick={() => handlePlanSelect("monthly")}
            className={
              selectedPlan === "monthly"
                ? classes.planCardSelected
                : classes.planCard
            }
            role="button"
            tabIndex={0}
            aria-label="Select monthly subscription plan"
            onKeyDown={(e) => e.key === "Enter" && handlePlanSelect("monthly")}
          >
            <Typography className={classes.planName}>
              {SUBSCRIPTION_PLANS.monthly.name}
            </Typography>
            <Divider style={{ margin: "8px 0" }} />
            <Typography className={classes.planPrice}>
              {SUBSCRIPTION_PLANS.monthly.price}
            </Typography>
            <Typography className={classes.planDescription}>
              {SUBSCRIPTION_PLANS.monthly.description}
            </Typography>
            {/* Invisible spacer to match Annual card height */}
            <div style={{ marginTop: "8px", height: "23px" }} />
          </Card>
        </Grid>

        {/* Annual Plan */}
        <Grid item xs={6} style={{ display: "flex", justifyContent: "center" }}>
          <Card
            onClick={() => handlePlanSelect("annual")}
            className={
              selectedPlan === "annual"
                ? classes.planCardSelected
                : classes.planCard
            }
            role="button"
            tabIndex={0}
            aria-label="Select annual subscription plan"
            onKeyDown={(e) => e.key === "Enter" && handlePlanSelect("annual")}
          >
            <Typography className={classes.planName}>
              {SUBSCRIPTION_PLANS.annual.name}
            </Typography>
            <Divider style={{ margin: "8px 0" }} />
            <Typography className={classes.planPrice}>
              {SUBSCRIPTION_PLANS.annual.price}
            </Typography>
            <Typography className={classes.planDescription}>
              {SUBSCRIPTION_PLANS.annual.description}
            </Typography>
            <div className={classes.savingsBadge}>
              {SUBSCRIPTION_PLANS.annual.savingsText}
            </div>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Typography className={classes.errorText}>{error}</Typography>
      )}

      <div className={classes.buttonContainer}>
        <Button
          theme={theme}
          onClick={handleProceedToPayment}
          disabled={!selectedPlan}
        >
          Continue to Payment
        </Button>
      </div>
    </div>
  );

  /**
   * Render Payment Stage (Stage 2)
   */
  const renderPayment = () => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];

    return (
      <div className={classes.modal}>
        <Typography className={classes.title}>
          Complete Your Payment
        </Typography>
        <Typography className={classes.subtitle}>
          {plan?.name} Plan - {plan?.price} {plan?.description}
        </Typography>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: theme?.primaryColor }} />
            <Typography style={{ marginTop: 16 }}>
              Processing your subscription...
            </Typography>
          </div>
        ) : (
          <div className={classes.paypalContainer}>
            <PayPalScriptProvider options={PAYPAL_OPTIONS}>
              <PayPalButtons
                style={{
                  shape: "pill",
                  color: "gold",
                  layout: "vertical",
                  label: "subscribe",
                }}
                createSubscription={createSubscription}
                onApprove={onSubscriptionApprove}
                onError={onSubscriptionError}
                onCancel={onSubscriptionCancel}
              />
            </PayPalScriptProvider>
          </div>
        )}

        {error && (
          <Typography className={classes.errorText}>{error}</Typography>
        )}

        {!loading && (
          <Typography
            className={classes.backButton}
            onClick={handleBackToSelection}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleBackToSelection()}
          >
            ← Back to plan selection
          </Typography>
        )}
      </div>
    );
  };

  /**
   * Render Success Stage (Stage 3)
   */
  const renderSuccess = () => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];

    return (
      <div className={classes.modal}>
        <div className={classes.successContainer}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography className={classes.title}>
            Subscription Activated!
          </Typography>
          <Typography className={classes.subtitle}>
            Welcome to BridgePal Kids! Your {plan?.name} subscription is now active.
          </Typography>
          <div className={classes.buttonContainer}>
            <Button theme={theme} onClick={handleClose}>
              Start Exploring
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render appropriate stage content
   */
  const renderModalContent = () => {
    switch (stage) {
      case 1:
        return renderPlanSelection();
      case 2:
        return renderPayment();
      case 3:
        return renderSuccess();
      default:
        return renderPlanSelection();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="payment-signup-modal"
      aria-describedby="subscription-payment-form"
    >
      {renderModalContent()}
    </Modal>
  );
}
